import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import WorldMap from "../components/WorldMap";
import CountdownTimer from "../components/CountdownTimer";
import AIRecommendations from "../components/AIRecommendations";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Wallet,
  TrendingUp,
  Coins,
  Clock,
  ArrowUpRight,
  ExternalLink,
  FileCheck,
  Rocket,
  ShoppingCart,
  Trophy,
  PieChart,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#0F2744", "#3B82F6", "#10B981"];

function PortfolioGrowthChart({ data }) {
  if (!data || data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={240}>
      <AreaChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
        <defs>
          <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="period" tick={{ fontSize: 12, fill: "#64748b" }} />
        <YAxis
          tick={{ fontSize: 11, fill: "#64748b" }}
          tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
        />
        <Tooltip
          formatter={(v) =>
            new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v)
          }
          contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke="#3B82F6"
          strokeWidth={2.5}
          fill="url(#growthGrad)"
          dot={{ r: 5, fill: "#0F2744", stroke: "#fff", strokeWidth: 2 }}
          activeDot={{ r: 7 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default function InvestorDashboard() {
  const [projects, setProjects] = useState([]);
  const [mapData, setMapData] = useState(null);
  const [tracker, setTracker] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, mapRes, trackerRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/map-data`),
        axios.get(`${API}/portfolio/tracker`),
      ]);
      setProjects(projectsRes.data);
      setMapData(mapRes.data);
      setTracker(trackerRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(v);

  const getScoreColor = (score) => {
    if (score >= 800) return "text-emerald-600";
    if (score >= 600) return "text-blue-600";
    if (score >= 400) return "text-amber-600";
    return "text-red-600";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="investor-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Investor Dashboard</h1>
          <p className="text-slate-500 mt-1">
            Track your portfolio, upcoming launches and investment opportunities
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => navigate("/buy-tokens")}
            className="bg-[#0F2744] hover:bg-[#1E3A5F] text-white"
            data-testid="buy-tokens-cta"
          >
            <ShoppingCart className="w-4 h-4 mr-2" /> Buy Tokens
          </Button>
          <Button
            onClick={() => navigate("/projects")}
            variant="outline"
            data-testid="browse-projects-btn"
          >
            View Projects <ArrowUpRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Portfolio Tracker Section */}
      {tracker && (
        <>
          {/* Portfolio Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="stat-portfolio-value">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Portfolio Value</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{fmt(tracker.total_current_value)}</p>
                    <p className="text-xs text-slate-500 mt-1">{tracker.total_tokens} tokens across {tracker.num_projects} projects</p>
                  </div>
                  <div className="w-11 h-11 bg-[#E8F4FD] rounded-xl flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-[#3B82F6]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="stat-projected-24m">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Projected (24 mo)</p>
                    <p className="text-2xl font-bold text-emerald-600 mt-1">{fmt(tracker.projected_value_24m)}</p>
                    <p className="text-xs text-emerald-600 mt-1 font-medium">+{tracker.total_gain_pct}% growth</p>
                  </div>
                  <div className="w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="stat-total-gain">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Projected Gain</p>
                    <p className="text-2xl font-bold text-emerald-600 mt-1">+{fmt(tracker.total_gain_24m)}</p>
                    <p className="text-xs text-slate-500 mt-1">Over 24 months</p>
                  </div>
                  <div className="w-11 h-11 bg-amber-50 rounded-xl flex items-center justify-center">
                    <Coins className="w-5 h-5 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="stat-best-performer">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Best Performer</p>
                    <p className="text-lg font-bold text-slate-900 mt-1">
                      {tracker.best_performer?.token_symbol}
                    </p>
                    <p className="text-xs text-emerald-600 mt-1 font-medium">
                      +{tracker.best_performer?.growth_24m}% (24mo)
                    </p>
                  </div>
                  <div className="w-11 h-11 bg-amber-50 rounded-xl flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Portfolio Growth Chart + Holdings */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2 border-slate-200" data-testid="portfolio-growth-chart">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-base font-semibold text-slate-900">
                  Portfolio Growth Projection
                </CardTitle>
                {tracker.is_demo && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                    Demo Portfolio
                  </span>
                )}
              </CardHeader>
              <CardContent>
                <PortfolioGrowthChart data={tracker.growth_timeline} />
              </CardContent>
            </Card>

            <Card className="border-slate-200" data-testid="holdings-allocation">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <PieChart className="w-4 h-4 text-[#3B82F6]" /> Holdings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {tracker.holdings.map((h, i) => (
                  <div
                    key={h.project_id}
                    className="p-3 rounded-lg border border-slate-100 hover:border-blue-200 transition-colors cursor-pointer"
                    onClick={() => navigate(`/projects/${h.project_id}`)}
                    data-testid={`holding-${h.token_symbol}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[i % COLORS.length] }}
                        />
                        <span className="font-semibold text-sm text-slate-900">
                          {h.token_symbol}
                        </span>
                        <span className="text-xs text-slate-400">{h.allocation_pct}%</span>
                      </div>
                      <span className="text-sm font-semibold text-slate-900">{fmt(h.current_value)}</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-slate-500">
                      <span>{h.tokens} tokens</span>
                      <span className="text-emerald-600 font-medium">
                        +{h.growth_24m}% (24mo)
                      </span>
                    </div>
                    {/* mini progress bar for projected growth */}
                    <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${Math.min(h.growth_24m * 1.5, 100)}%`,
                          backgroundColor: COLORS[i % COLORS.length],
                        }}
                      />
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  className="w-full text-sm mt-2"
                  onClick={() => navigate("/wallet")}
                  data-testid="view-wallet-btn"
                >
                  View Full Wallet <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* AI Investment Recommendations */}
      <AIRecommendations />

      {/* World Map */}
      {mapData && <WorldMap projects={mapData.projects} />}

      {/* Upcoming Launches */}
      <Card className="border-slate-200" data-testid="upcoming-launches">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">
            Upcoming Token Launches
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {projects.map((project) => (
              <CountdownTimer
                key={project.id}
                launchDate={project.launch_date}
                projectName={project.name}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Project Cards */}
      <Card className="border-slate-200" data-testid="project-opportunities">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-slate-900">
            Investment Opportunities
          </CardTitle>
          <Button
            variant="ghost"
            className="text-[#3B82F6] hover:text-[#0F2744]"
            onClick={() => navigate("/projects")}
            data-testid="view-all-projects-btn"
          >
            View All <ExternalLink className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {projects.map((project) => {
              const score = project.score_data?.score || 0;
              return (
                <div
                  key={project.id}
                  className="p-4 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors cursor-pointer"
                  onClick={() => navigate(`/projects/${project.id}`)}
                  data-testid={`project-${project.token_symbol}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <img src={project.image_url} alt={project.name} className="w-12 h-12 rounded-lg object-cover" />
                      <div>
                        <h4 className="font-semibold text-slate-900">{project.name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="industry-tag">{project.industry}</span>
                          <span className="text-xs text-slate-500 font-mono">{project.token_symbol}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${getScoreColor(score)}`}>Score: {score}/1000</p>
                      <p className="text-sm text-slate-500 flex items-center justify-end gap-1">
                        <FileCheck className="w-3 h-3" />
                        {project.score_data?.completion_percentage || 0}% complete
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Token Price</p>
                      <p className="font-semibold text-slate-900">{fmt(project.token_price)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Supply</p>
                      <p className="font-semibold text-slate-900">{project.token_supply.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Expected ROI</p>
                      <p className="font-semibold text-emerald-600">{project.roi_percentage}%</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Launch</p>
                      <p className="font-semibold text-slate-900">
                        {new Date(project.launch_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">{project.location}</span>
                      <span className="bg-[#0F2744] text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Launching Soon
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50" data-testid="info-card">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <FileCheck className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">AI-Powered Due Diligence</p>
              <p className="text-sm text-blue-700 mt-1">
                Each project is scored out of 1000 based on 38 key documents across 10 categories
                including legal compliance, financial validation, and asset verification. Higher
                scores indicate better investor protection and project readiness.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
